import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { detectPlatform, extractYouTubeId, getEmbedUrl, videoKey } from '../utils/video';
import { Art, BeltRank, TechniqueType, Uniform } from '../types';
import { ARTS, getTierLabelsFor, impliedUniform, sortArts, tierRungs } from '../data/arts';
import { categoryInArts } from '../data/categories';
import SuggestInput from './SuggestInput';
import type { Category, Technique } from '../types';

const API_BASE = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || "";

interface AddTechniqueModalProps {
  categories: Category[];
  inline?: boolean;
  /** Everything already in the library — a link that's already here is refused by name. */
  existing?: Technique[];
  /** `attribution` is the athlete in the clip when that's not the poster — the
   *  poster handle is scraped on submission, this is the one thing only the
   *  submitter knows. Empty when they left it blank. */
  onAdd: (name: string, videoUrl: string, categoryId: string, tier: BeltRank, type: TechniqueType, uniform: Uniform, arts: Art[], attribution: string, context: string) => void;
  onCancel: () => void;
}

async function validateVideoLink(url: string): Promise<boolean> {
  const platform = detectPlatform(url);
  if (!platform) return false;

  // YouTube and TikTok embeds are reliable — a resolvable video ID is
  // sufficient. TikTok needs the full tiktok.com/@user/video/ID form; vm./vt.
  // short links carry no id to embed, so they're rejected here.
  if (platform === 'youtube') return extractYouTubeId(url) !== null;
  if (platform === 'tiktok') return getEmbedUrl(url) !== null;

  // Instagram needs server-side validation
  try {
    const endpoint = API_BASE
      ? `${API_BASE}?url=${encodeURIComponent(url)}`
      : `/api/ig-video?url=${encodeURIComponent(url)}`;
    const { auth } = await import('../firebase');
    const token = await auth.currentUser?.getIdToken();
    const ctrl = new AbortController();
    // Cold-started function + Instagram fetch can take >10s — don't let the
    // timeout misreport a valid link as unsupported.
    const timeout = setTimeout(() => ctrl.abort(), 25000);
    const resp = await fetch(endpoint, {
      signal: ctrl.signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    clearTimeout(timeout);
    const data = await resp.json();
    return !!data.videoUrl;
  } catch {
    return false;
  }
}

export default function AddTechniqueModal({ categories, inline, existing = [], onAdd, onCancel }: AddTechniqueModalProps) {
  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [attribution, setAttribution] = useState('');
  // The entry this clip teaches the move from. Deliberately not part of the
  // name: keeping it separate is what lets every "Armbar" stack under one card
  // while the versions list still says which entry each one is off.
  const [context, setContext] = useState('');
  // Every select starts empty and Add stays disabled until all of them are
  // answered. A pre-filled dropdown is a wrong answer nobody reads — the old
  // defaults (first position, White Belt, Concept, Gi) were exactly that.
  const [categoryId, setCategoryId] = useState('');
  const [tier, setTier] = useState<BeltRank | ''>('');
  const [type, setType] = useState<TechniqueType | ''>('');
  const [uniform, setUniform] = useState<Uniform | ''>('');
  const [arts, setArts] = useState<Art[]>([]);
  const [validating, setValidating] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  // What the library already says, offered as you type. A submission that
  // reuses an existing spelling stacks onto the card it belongs to instead of
  // starting a near-duplicate of it.
  const suggestions = useMemo(() => {
    const uniq = (pick: (t: Technique) => string | null | undefined) =>
      [...new Set(existing.map((t) => pick(t)?.trim()).filter((v): v is string => !!v))]
        .sort((a, b) => a.localeCompare(b));
    return {
      names: uniq((t) => t.name),
      contexts: uniq((t) => t.context),
      attributions: uniq((t) => t.attribution),
    };
  }, [existing]);

  // Positions offered are scoped to the picked sports: wrestling names them
  // from the top (rides and turns), BJJ from the bottom (guards), so the two
  // vocabularies barely overlap. A clip tagged for both gets the union.
  const positions = arts.length > 0 ? categories.filter((c) => categoryInArts(c, arts)) : [];

  const toggleArt = (a: Art) => {
    const next = arts.includes(a) ? arts.filter((x) => x !== a) : sortArts([...arts, a]);
    setArts(next);
    // Dropping a sport can take the chosen position out of the list — clear it
    // rather than submit a position the form no longer offers.
    if (categoryId && !categories.some((c) => c.id === categoryId && categoryInArts(c, next))) {
      setCategoryId('');
    }
  };

  // Judo is a gi sport and Wrestling a no-gi one, so for those the answer is
  // already known — fill it in and don't ask. Only a selection that's genuinely
  // trained both ways (anything with BJJ in it) gets the field.
  const implied = impliedUniform(arts);
  const chosenUniform = implied ?? uniform;
  const complete = !!name.trim() && !!link.trim() && !!categoryId && tier !== '' && !!type && !!chosenUniform && arts.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // `complete` covers the empty selects, and TS carries that narrowing
    // through to the onAdd call below.
    if (!complete || validating) return;
    // Same clip already in the library? Refuse before the network round-trip.
    // This is what catches a stale link — the browser autofilling the last
    // one, or a Copy Link that silently left the old URL on the clipboard.
    const key = videoKey(link.trim());
    const dupe = key ? existing.find((t) => t.instagramUrl && videoKey(t.instagramUrl) === key) : undefined;
    if (dupe) {
      setLink('');
      setLinkError(`Already in the library as "${dupe.name}"`);
      return;
    }
    setValidating(true);
    const ok = await validateVideoLink(link.trim());
    setValidating(false);
    if (!ok) {
      setLink('');
      setLinkError("This link isn't supported");
      return;
    }
    onAdd(name.trim(), link.trim(), categoryId, tier, type, chosenUniform, arts, attribution.trim(), context.trim());
  };

  // Belt names follow the chips, so a wrestling clip never shows BJJ belts.
  const tierLabels = getTierLabelsFor(arts);

  const typeLabels = {
    [TechniqueType.Submission]: 'Submission',
    [TechniqueType.Sweep]: 'Sweep',
    [TechniqueType.Pass]: 'Pass',
    [TechniqueType.Takedown]: 'Takedown',
    [TechniqueType.Escape]: 'Escape',
    [TechniqueType.Counter]: 'Counter',
    [TechniqueType.Transition]: 'Transition',
    [TechniqueType.Drill]: 'Drill',
    [TechniqueType.Concept]: 'Concept'
  };

  const card = (
    <div className="modal modal--add" onClick={(e) => e.stopPropagation()} style={inline ? { width: 380 } : undefined}>
      <div className="modal-head">
        <div className="modal-title">Add New Technique</div>
        <button type="button" className="modal-close" onClick={onCancel} aria-label="Close">
          <X size={18} />
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <label className="modal-label">Sports</label>
        <div className="modal-art-chips">
          {ARTS.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`category-filter-chip${arts.includes(a.id) ? ' category-filter-chip--active' : ''}`}
              onClick={() => toggleArt(a.id)}
            >
              {a.shortLabel}
            </button>
          ))}
        </div>

        <label className="modal-label" htmlFor="add-tech-name">Technique Name</label>
        <SuggestInput
          id="add-tech-name"
          className="modal-input"
          placeholder="e.g. Triangle Choke"
          options={suggestions.names}
          value={name}
          onChange={setName}
        />

        <label className="modal-label" htmlFor="add-tech-context">Context</label>
        <SuggestInput
          id="add-tech-context"
          className="modal-input"
          placeholder="Optional, e.g. off a failed arm drag"
          options={suggestions.contexts}
          value={context}
          onChange={setContext}
        />

        {/* The four selects pair off two to a line — they're all short, and the
            form runs past the frame if each takes a row of its own. */}
        <div className="modal-row">
          <div>
            <label className="modal-label" htmlFor="add-tech-category">Position</label>
            <select
              id="add-tech-category"
              className="modal-input"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="" disabled>{arts.length === 0 ? 'Pick a sport first…' : 'Choose…'}</option>
              {positions.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="modal-label" htmlFor="add-tech-type">Type</label>
            <select
              id="add-tech-type"
              className="modal-input"
              value={type}
              onChange={(e) => setType(e.target.value as TechniqueType)}
            >
              <option value="" disabled>Choose…</option>
              {Object.entries(typeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Level pairs with Uniform when the uniform is still a question, and
            takes the full row on its own when the sport already answered it. */}
        <div className={implied ? undefined : 'modal-row'}>
          <div>
            <label className="modal-label" htmlFor="add-tech-tier">Difficulty</label>
            <select
              id="add-tech-tier"
              className="modal-input"
              value={tier}
              onChange={(e) => setTier(Number(e.target.value) as BeltRank)}
            >
              <option value="" disabled>Choose…</option>
              {tierRungs(tierLabels).map((r) => (
                <option key={r.tier} value={r.tier}>{r.label}</option>
              ))}
            </select>
          </div>
          {!implied && (
            <div>
              <label className="modal-label" htmlFor="add-tech-uniform">Uniform</label>
              <select
                id="add-tech-uniform"
                className="modal-input"
                value={uniform}
                onChange={(e) => setUniform(e.target.value as Uniform)}
              >
                <option value="" disabled>Choose…</option>
                <option value={Uniform.Gi}>Gi</option>
                <option value={Uniform.NoGi}>No-Gi</option>
              </select>
            </div>
          )}
        </div>

        <label className="modal-label" htmlFor="add-tech-link">Video Link</label>
        <input
          id="add-tech-link"
          className={`modal-input${linkError ? ' modal-input--error' : ''}`}
          type="url"
          autoComplete="off"
          placeholder={linkError ?? 'Instagram, YouTube or TikTok link'}
          value={link}
          onChange={(e) => { setLink(e.target.value); setLinkError(null); }}
        />

        <label className="modal-label" htmlFor="add-tech-attribution">Athlete Featured</label>
        <SuggestInput
          id="add-tech-attribution"
          className="modal-input"
          placeholder="Optional, if it isn't the creator"
          options={suggestions.attributions}
          value={attribution}
          onChange={setAttribution}
        />
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn--cancel" onClick={onCancel}>Cancel</button>
          <button type="submit" className="modal-btn modal-btn--add" disabled={!complete || validating}>
            {validating ? 'Validating...' : 'Add'}
          </button>
        </div>
      </form>
    </div>
  );

  if (inline) return card;

  return (
    <div className="modal-backdrop">
      {card}
    </div>
  );
}
